// AGC: useGpsAutoCenter - Reusable hook to auto-center map pages on the user's
// AGC: current GPS location once on mount.
// AGC:
// AGC: Behavior:
// AGC:   - On native (Capacitor): uses internal GPS via @capacitor/geolocation.
// AGC:   - On web: requests browser geolocation permission via navigator.geolocation.
// AGC: Failures (denied permission, timeout, unavailable) are silently logged so
// AGC: the page falls back to whatever default center it was initialized with.
// AGC:
// AGC: Pass `enabled = false` whenever the page already has a meaningful location
// AGC: to show (e.g. an existing lot polygon, a stored farm coordinate the user
// AGC: explicitly chose to view, etc.) so we don't yank the viewport away from it.

import { useEffect, useRef } from 'react'
import { getCurrentPosition, type GeoCoords } from '../services/nativeGeolocation'

interface UseGpsAutoCenterOptions {
  // AGC: When false, the hook is a no-op. Use this to skip when the page already
  // AGC: has a meaningful preferred location.
  enabled: boolean
  // AGC: Called once with the device coordinates if/when GPS resolves successfully.
  onLocate: (coords: GeoCoords) => void
  // AGC: GPS request timeout (ms). Defaults to 8000.
  timeout?: number
}

export const useGpsAutoCenter = ({ enabled, onLocate, timeout = 8000 }: UseGpsAutoCenterOptions): void => {
  // AGC: Guard against double-fire under React 18 StrictMode and prop changes.
  const firedRef = useRef(false)
  // AGC: Keep latest callback in a ref so we don't restart the effect when it
  // AGC: changes identity between renders.
  const onLocateRef = useRef(onLocate)
  onLocateRef.current = onLocate

  useEffect(() => {
    if (!enabled || firedRef.current) return
    firedRef.current = true

    let cancelled = false

    void (async () => {
      try {
        const coords = await getCurrentPosition({
          enableHighAccuracy: true,
          timeout,
          maximumAge: 5000,
        })
        if (!cancelled) {
          onLocateRef.current(coords)
        }
      } catch (err) {
        // AGC: Silent failure - the page keeps its default center. Logged for
        // AGC: diagnostics only.
        console.warn('AGC: useGpsAutoCenter: could not auto-center on device location:', err)
      }
    })()

    return () => {
      cancelled = true
    }
  }, [enabled, timeout])
}
